import React, { useMemo, useState } from 'react';
import {Badge, Card, CardContent, CardHeader, Container, Grid, MenuItem, Stack, Tooltip, Typography} from '@mui/material';
import {
	Instant,
	Now, NumberField,
	PlainDate,
	PlainDatePicker,
	PlainDateTime,
	PlainDateTimePicker,
	TextField
} from "@variocube/app-ui";
import BlockIcon from '@mui/icons-material/Block';
import {B2Site, SiteAccessibility} from "../../data/client.gen";
import {useOrder} from "../../context/order";
import {useLocalization} from "../../i18n";
import {Accessibility} from "../../component/accessibility";
import {BoxPackagePrice} from "../../data/sites";
import {computePackages, notBlank} from "../../utils";
import {getStartOfDay, getWeekdayFromDate, isValidPeriod, parseTime} from "../../dates-utils";
import {DisplayUnitsCount} from "../../booking-utils";
import {IntegerNumberFormat} from "@variocube/app-ui/esm/Input/NumberField";
import {OrderNav} from "./order-nav";
import {useNavigate} from "react-router-dom";
import {useTenant} from "../../context/tenant";

interface OrderDatesProps {
	site: B2Site;
}

export function OrderDates({site}: OrderDatesProps) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {tenant} = useTenant();
	const {order: { dates: { datesValid = false, unitCounts = 0 }}} = useOrder();

	return (
		<Container maxWidth="sm">
			<Card>
				<CardHeader
					title={(
						<Grid
                            container
                            justifyContent="space-between"
                            alignItems="baseline"
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                sm="auto"
                            >
                                <Typography variant="h4">{t('dates.period')}</Typography>
                            </Grid>
                            {(datesValid && unitCounts > 0) && (
								<Grid
									item
									xs={12}
									sm="auto"
								>
									<Typography variant="subtitle1">
										{t('dates.duration')}
										&nbsp;
										<DisplayUnitsCount unit={site.bookingUnit} count={unitCounts} />
									</Typography>
								</Grid>
							)}
                        </Grid>
					)}
				/>
				<CardContent>
					{site.bookingUnit === 'Days' && (
						<DayTimeframeSelector site={site} />
					)}
					{site.bookingUnit === 'Hours' && (
						<HourTimeframeSelector site={site} />
					)}
					<Stack
						mt={2}
						direction="row"
						justifyContent="space-between"
					>
						<Typography>{t('sites.accessibility')}:</Typography>
						<Accessibility site={site} />
					</Stack>
				</CardContent>
			</Card>

			<OrderNav
				site={site}
				onNext={() => navigate(`/${tenant?.id}/${site.id}/boxes`)}
				disabled={!datesValid}
			/>
		</Container>
	)
}

function getDefaultFrom(minDate: PlainDateTime|undefined, accessibility: SiteAccessibility) {
	const now = Now.plainDateTimeISO();
	if (minDate !== undefined && PlainDateTime.compare(minDate, now) > 0) {
		console.log("defaultFrom: minDate is after `now`. defaulting to `minDate`", { minDate, now });
		return minDate;
	}
    if (accessibility !== undefined) {
        const yesterday = now.subtract({ days: 1 });
        const yesterdayAccessibility = accessibility[getWeekdayFromDate(yesterday.toPlainDate())];
        if (yesterdayAccessibility && yesterdayAccessibility.from && yesterdayAccessibility.until) {
            if (yesterdayAccessibility.until < yesterdayAccessibility.from) {
                const until = parseTime(yesterdayAccessibility.until);
                if (until) {
					const targetUntil = now.with({
						hour: until.hour,
						minute: until.minute,
						second: until.second,
					});
                    if (PlainDateTime.compare(now, targetUntil) < 0) {
						console.log("defaultFrom: yesterday's accessibility is not over. using yesterday");
						return getStartOfDay(yesterday.toPlainDate());
                    }
                }
            }
        }
    }
    console.log("defaultFrom: defaulting to `now`");
    return getStartOfDay(now.toPlainDate());
}

function DayTimeframeSelector({site}: { site: B2Site }) {
	const {
        activeFrom,
        activeUntil,
        notBookableDates: nbD = [],
        maxBookingUnits,
        accessibility,
        boxes,
    } = site;
	const {t, language} = useLocalization();
    const packages = computePackages(boxes);
    const notBookableDates = nbD.map(d => PlainDate.from(d));
    const activeFromDateTime = notBlank(activeFrom) ? getStartOfDay(PlainDate.from(activeFrom as string)) : undefined;
    const activeUntilDateTime = notBlank(activeUntil) ? getStartOfDay(PlainDate.from(activeUntil as string)) : undefined;

    const defaultFrom = getDefaultFrom(activeFromDateTime, accessibility);

	const {order: { dates: { from, until }}, setDates} = useOrder();
	const {orderFrom, orderUntil} = useMemo(() => ({
		orderFrom: (notBlank(from))
			? PlainDateTime.from(from as string)
			: defaultFrom,
		orderUntil: (notBlank(until))
			? PlainDateTime.from(until as string)
			: undefined,
	}), [from, until, defaultFrom])

    const maxDate = useMemo(() => {
        const maxBookingUntil = !!maxBookingUnits
			? orderFrom.add({ days: maxBookingUnits - 1 })
			: undefined;
        if (maxBookingUntil !== undefined && activeUntilDateTime !== undefined) {
			// return the earlier until date
			return PlainDateTime.compare(maxBookingUntil, activeUntilDateTime) < 0
				? maxBookingUntil
				: activeUntilDateTime;
        }
        return activeUntilDateTime ?? maxBookingUntil;
    }, [orderFrom, maxBookingUnits, activeUntilDateTime]);

	function updateOrderDates(from: PlainDate|undefined, until: PlainDate|undefined) {
		const siteMaxDate = site.pricingMode === 'Package' ? undefined : maxDate?.toPlainDate();
		setDates({
			from: from?.toJSON(),
			until: until?.toJSON(),
			unitCounts: !from || !until ? 0 : from.until(until).days + 1,
			datesValid: isValidPeriod(from, until, activeFromDateTime?.toPlainDate(), siteMaxDate)
		});
	}

	function handleFromChange(from: PlainDate|null) {
		updateOrderDates(from ?? undefined, orderUntil?.toPlainDate());
	}

	function handleUntilChange(until: PlainDate|null) {
		updateOrderDates(orderFrom.toPlainDate(), until ?? undefined);
	}

    const [selectUnits, setSelectUnits] = useState(0);
	function handleUnitChange(units: number) {
		setSelectUnits(units);
		handleUntilChange(orderFrom.add({ days: units - 1 }).toPlainDate());
	}

    return (
        <Grid
            container
            spacing={2}
        >
            <Grid
                item
                xs={12}
                md={6}
            >
				<PlainDatePicker
					required
					fullWidth
					locale={language}
					label={t('dates.from.label')}
					helperText={t('dates.from.hint')}
					value={orderFrom.toPlainDate()}
					onChange={handleFromChange}
					disablePast={true}
					renderDay={day =>
						notBookableDates.find(d => PlainDate.compare(d, day) === 0)
							? (
								<Tooltip title={t('dates.unavailable')}>
									<Badge badgeContent={<BlockIcon sx={{ fontSize: '15px', position: 'relative', top: -1, right: -2 }} color="error" />}>
										<span>{day.day}</span>
									</Badge>
								</Tooltip>
							)
							: <span>{day.day}</span>
					}
				/>
            </Grid>
            <Grid
                item
                xs={12}
                md={6}
            >
                {'Package' === site?.pricingMode && (
                    <TextField
                        select fullWidth required
                        label={t('dates.package')}
                        value={selectUnits}
                        onChange={value => handleUnitChange(parseInt(value))}
                        InputLabelProps={{
                            shrink: true
                        }}
                    >
                        {packages.map(({ units }, i) => (
                            <MenuItem key={'package-' + i} value={units}><DisplayUnitsCount unit={site.bookingUnit} count={units} /></MenuItem>
                        ))}
                    </TextField>
                )}
                {'Unit' === site.pricingMode && (
					<PlainDatePicker
						required
						fullWidth
						locale={language}
						label={t('dates.until.label')}
						helperText={t('dates.until.hint')}
						value={orderUntil?.toPlainDate() ?? null}
						onChange={handleUntilChange}
						disablePast={true}
						minDate={orderFrom.toPlainDate()}
						maxDate={maxDate?.toPlainDate()}
						renderDay={day =>
							notBookableDates.find(d => PlainDate.compare(d, day) === 0)
								? (
									<Tooltip title={t('dates.unavailable')}>
										<Badge badgeContent={<BlockIcon sx={{ fontSize: '15px', position: 'relative', top: -1, right: -2 }} color="error" />}>
											<span>{day.day}</span>
										</Badge>
									</Tooltip>
								)
								: <span>{day.day}</span>
						}
					/>
                )}
            </Grid>
        </Grid>
    )
}

function HourTimeframeSelector({site}: { site: B2Site }) {
	const {
		effectiveAccessibleFrom,
		effectiveAccessibleUntil,
		maxBookingUnits,
		boxes,
		zoneId
	} = site;
	const {t, language} = useLocalization();
	const packages = computePackages(boxes);

	const minDate = notBlank(effectiveAccessibleFrom)
		? Instant.from(effectiveAccessibleFrom as string).toZonedDateTimeISO(zoneId ?? 'UTC').toPlainDateTime()
		: undefined;
	const maxDate = notBlank(effectiveAccessibleUntil)
		? Instant.from(effectiveAccessibleUntil as string).toZonedDateTimeISO(zoneId ?? 'UTC').toPlainDateTime()
		: undefined;
	const now = Now.plainDateTimeISO();

	const defaultFrom = (minDate !== undefined && PlainDateTime.compare(minDate, now) > 0)
		? minDate
		: now;

	const {order: { dates: { fromDateTime, hours }}, setDates} = useOrder();
	const {orderFrom} = useMemo(() => ({
		orderFrom: (notBlank(fromDateTime))
			? Instant.from(fromDateTime as string).toZonedDateTimeISO(zoneId ?? 'UTC').toPlainDateTime()
			: defaultFrom,
	}), [fromDateTime, hours, defaultFrom, zoneId])

	function updateOrderDates(from: PlainDateTime|undefined, hours: number|undefined) {
		const fromDateTime = from?.toZonedDateTime(zoneId ?? 'UTC')?.toInstant().toJSON();
		setDates({
			fromDateTime,
			hours,
			unitCounts: !hours ? 0 : hours,
			datesValid: from !== undefined && hours !== undefined && hours > 0
		});
		console.log('order dates changed, fromDateTime', fromDateTime)
	}

	function handleFromChange(from: PlainDateTime|null) {
		updateOrderDates(from ?? undefined, hours);
	}

	function handleHoursChange(h: number|null) {
		let parsedHours = h ?? 0;
        if (parsedHours < 0) {
			parsedHours = 0;
		}
        else if (maxBookingUnits && parsedHours > maxBookingUnits) {
			parsedHours = maxBookingUnits;
		}
		updateOrderDates(orderFrom, parsedHours ?? undefined);
	}

	return (
		<Grid
			container
			spacing={2}
		>
			<Grid
				item
				xs={12}
				md={6}
			>
				<PlainDateTimePicker
					required
					fullWidth
					locale={language}
					label={t('dates.from.label')}
					helperText={t('dates.from.hint')}
					value={orderFrom}
					onChange={handleFromChange}
					disablePast={true}
					minDateTime={minDate}
					maxDateTime={maxDate}

					// minutesStep={60}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				{'Package' === site?.pricingMode && (
					<TextField
						select fullWidth required
						label={t('dates.package')}
						value={hours}
						onChange={value => handleHoursChange(parseInt(value))}
						InputLabelProps={{
							shrink: true
						}}
					>
						{packages.map(({ units }, i) => (
							<MenuItem key={'package-' + i} value={units}><DisplayUnitsCount unit={site.bookingUnit} count={units} /></MenuItem>
						))}
					</TextField>
				)}
				{'Unit' === site?.pricingMode && (
					<NumberField
						required
						fullWidth
						label={t('dates.hours')}
						numberFormat={IntegerNumberFormat}
						numberValue={hours ?? null}
						onChangeNumber={handleHoursChange}
						InputLabelProps={{
							shrink: true
						}}
					 />
				)}
			</Grid>
		</Grid>
	);
}
